import type { RouteObject } from 'react-router';
import { lazy } from 'react';
import { Navigate } from 'react-router';
import UserLayout from '../pages/User/components/UserLayout';
import AspiceLayout from '../pages/Aspice';
import Forbidden from '../pages/403';
import BasicLayout from '../layouts/BasicLayout';
import AuthGuard from '../components/AuthGuard';
import { useAccess } from '@/hooks/useAccess';

// 页面组件按需加载，配合各布局 <Outlet /> 外层的 <Suspense> 实现代码分割；
// 布局（BasicLayout/UserLayout/AspiceLayout）、AuthGuard、Forbidden 保持同步加载，
// 因为它们构成路由外壳或被 AuthGuard 直接引用（403 已随 AuthGuard 打入主包）。
const Login = lazy(() => import('../pages/User/Login'));
const ForgotPassword = lazy(() => import('../pages/User/ForgotPassword'));
const Home = lazy(() => import('../pages/Home'));
const ProjectOverview = lazy(() => import('../pages/ProjectOverview'));
const ProjectInfo = lazy(() => import('../pages/Project/Info'));
const ProjectProcesses = lazy(() => import('../pages/Project/Processes'));
const TeamManagement = lazy(() => import('../pages/Project/TeamManagement'));
const ProcessDocs = lazy(() => import('../pages/DocCenter/ProcessDocs'));
const RecycleBin = lazy(() => import('../pages/DocCenter/RecycleBin'));
const ReviewTasks = lazy(() => import('../pages/ReviewTasks'));
const SystemOverview = lazy(() => import('../pages/System/Overview'));
const SystemUsers = lazy(() => import('../pages/System/Users'));
const AspiceProjects = lazy(() => import('../pages/Aspice/Projects'));
const SystemSettings = lazy(() => import('../pages/System/Settings'));
const AccountSettings = lazy(() => import('../pages/Account/Settings'));
const AccountNotifications = lazy(() => import('../pages/Account/Notifications'));
const DocEditor = lazy(() => import('../pages/DocCenter/DocEditor'));
const Attachment = lazy(() => import('../pages/DocCenter/Attachment'));
const AspiceOverview = lazy(() => import('../pages/Aspice/Overview'));
const AspiceTemplates = lazy(() => import('../pages/Aspice/Templates'));
const AspiceRoles = lazy(() => import('../pages/Aspice/Roles'));
const AspiceProcessDocs = lazy(() => import('../pages/Aspice/ProcessDocs'));
const ProductLibrary = lazy(() => import('../pages/ProductLibrary'));
const ProcessDocsPreview = lazy(() => import('../pages/ProcessDocs'));
const Help = lazy(() => import('../pages/Help'));
const Changelog = lazy(() => import('../pages/Changelog'));
const NotFound = lazy(() => import('../pages/404'));
const NoProcess = lazy(() => import('@/pages/DocCenter/ProcessDocs/components/NoProcess'));

type AccessKey = keyof ReturnType<typeof useAccess>;

interface RouteMeta {
  access?: AccessKey;
  name?: string;
  icon?: string;
  locale?: string;
  hideInMenu?: boolean;
}

type ExtendedRouteObject = RouteObject & {
  meta?: RouteMeta;
  children?: ExtendedRouteObject[];
};

const routes: ExtendedRouteObject[] = [
  {
    path: '/',
    element: <BasicLayout />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: '/overview',
        element: (
          <AuthGuard requiredAccess="normalUser">
            <ProjectOverview />
          </AuthGuard>
        ),
        meta: {
          name: 'overview',
          access: 'normalUser',
          icon: 'BarChartOutlined',
        },
      },
      {
        path: '/project',
        meta: {
          name: 'project-settings',
          access: 'normalUser',
          icon: 'ProjectOutlined',
        },
        children: [
          {
            path: '/project/info',
            element: (
              <AuthGuard requiredAccess="normalUser">
                <ProjectInfo />
              </AuthGuard>
            ),
            meta: {
              name: 'project-info',
              locale: 'menu.project-info',
            },
          },
          {
            path: '/project/processes',
            element: (
              <AuthGuard requiredAccess="normalUser">
                <ProjectProcesses />
              </AuthGuard>
            ),
            meta: {
              name: 'process-config',
              locale: 'menu.process-config',
            },
          },
          {
            path: '/project/team',
            element: (
              <AuthGuard requiredAccess="normalUser">
                <TeamManagement />
              </AuthGuard>
            ),
            meta: {
              name: 'team-management',
              locale: 'menu.team-management',
            },
          },
          {
            index: true,
            element: <Navigate to="/project/info" replace />,
          },
        ],
      },
      {
        path: '/docs',
        meta: {
          name: 'dev-repository',
          access: 'normalUser',
          icon: 'FileTextOutlined',
        },
        element: <NoProcess />,
        children: [],
      },
      {
        path: '/docs/process/:processCode',
        element: (
          <AuthGuard requiredAccess="normalUser">
            <ProcessDocs />
          </AuthGuard>
        ),
        meta: {
          hideInMenu: true,
        },
      },
      {
        path: '/docs/recycle-bin',
        element: (
          <AuthGuard requiredAccess="normalUser">
            <RecycleBin />
          </AuthGuard>
        ),
        meta: {
          hideInMenu: true,
        },
      },
      {
        path: '/system/overview',
        element: (
          <AuthGuard requiredAccess="canAdmin">
            <SystemOverview />
          </AuthGuard>
        ),
        meta: {
          name: 'system-overview',
          icon: 'DashboardOutlined',
          access: 'canAdmin',
        },
      },
      {
        path: '/system/users',
        element: (
          <AuthGuard requiredAccess="canAdmin">
            <SystemUsers />
          </AuthGuard>
        ),
        meta: {
          name: 'user-list',
          icon: 'TeamOutlined',
          access: 'canAdmin',
        },
      },
      {
        path: '/system/projects',
        element: (
          <AuthGuard requiredAccess="canAdmin">
            <AspiceProjects />
          </AuthGuard>
        ),
        meta: {
          name: 'project-list',
          icon: 'ProjectOutlined',
          access: 'canAdmin',
        },
      },
      {
        path: '/system/settings',
        element: (
          <AuthGuard requiredAccess="canAdmin">
            <SystemSettings />
          </AuthGuard>
        ),
        meta: {
          name: 'system-settings',
          icon: 'SettingOutlined',
          access: 'canAdmin',
        },
      },
      {
        path: '/account',
        meta: {
          name: 'personal-settings',
          icon: 'UserOutlined',
          hideInMenu: true,
        },
        children: [
          {
            path: '/account/settings',
            element: <AccountSettings />,
            meta: {
              name: 'account-info',
              locale: 'menu.account-info',
            },
          },
          {
            path: '/account/notifications',
            element: <AccountNotifications />,
            meta: {
              name: 'notifications',
              locale: 'menu.notifications',
            },
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: '/docs/:docId/attachments/:id',
    element: (
      <AuthGuard>
        <Attachment />
      </AuthGuard>
    ),
    meta: {
      hideInMenu: true,
    },
  },
  {
    path: '/review-tasks',
    element: (
      <AuthGuard>
        <ReviewTasks />
      </AuthGuard>
    ),
    meta: {
      name: 'review-tasks',
      hideInMenu: true,
    },
  },
  {
    path: '/doc-editor',
    element: (
      <AuthGuard>
        <DocEditor />
      </AuthGuard>
    ),
    meta: {
      hideInMenu: true,
    },
  },
  {
    path: '/aspice',
    // 布局本身不做整体拦截，具体页面通过各自的 AuthGuard 在 AspiceLayout 的
    // 内容区域内展示 403，从而保留侧边栏/头部不变
    element: <AspiceLayout />,
    meta: {
      hideInMenu: true,
      access: 'is_aspice_specialist',
    },
    children: [
      {
        path: '/aspice/overview',
        element: (
          <AuthGuard requiredAccess="is_aspice_specialist">
            <AspiceOverview />
          </AuthGuard>
        ),
        meta: {
          access: 'is_aspice_specialist',
        },
      },
      {
        path: '/aspice/projects',
        element: (
          <AuthGuard requiredAccess="is_aspice_specialist">
            <AspiceProjects />
          </AuthGuard>
        ),
        meta: {
          access: 'is_aspice_specialist',
        },
      },
      {
        path: '/aspice/templates',
        element: (
          <AuthGuard requiredAccess="is_aspice_specialist">
            <AspiceTemplates />
          </AuthGuard>
        ),
        meta: {
          access: 'is_aspice_specialist',
        },
      },
      {
        path: '/aspice/roles',
        element: (
          <AuthGuard requiredAccess="is_aspice_specialist">
            <AspiceRoles />
          </AuthGuard>
        ),
        meta: {
          access: 'is_aspice_specialist',
        },
      },
      {
        path: '/aspice/processdocs',
        element: (
          <AuthGuard requiredAccess="is_aspice_specialist">
            <AspiceProcessDocs />
          </AuthGuard>
        ),
        meta: {
          access: 'is_aspice_specialist',
        },
      },
    ],
  },
  {
    path: '/product-library',
    element: (
      <AuthGuard>
        <ProductLibrary />
      </AuthGuard>
    ),
    meta: {
      name: 'product-library',
      hideInMenu: true,
    },
  },
  {
    path: '/processdocs',
    element: (
      <AuthGuard>
        <ProcessDocsPreview />
      </AuthGuard>
    ),
    meta: {
      name: 'process-docs-preview',
      hideInMenu: true,
    },
  },
  {
    path: '/help',
    element: (
      <AuthGuard>
        <Help />
      </AuthGuard>
    ),
    meta: {
      name: 'help',
      hideInMenu: true,
    },
  },
  {
    path: '/changelog',
    element: (
      <AuthGuard>
        <Changelog />
      </AuthGuard>
    ),
    meta: {
      name: 'changelog',
      hideInMenu: true,
    },
  },
  {
    path: '/user',
    element: <UserLayout />,
    children: [
      {
        path: '/user/login',
        element: <Login />,
      },
      {
        path: '/user/forgot-password',
        element: <ForgotPassword />,
      },
    ],
  },
  {
    path: '/403',
    element: <Forbidden />,
  },
];

export default routes;
